import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { PlusCircle, Hammer, Calendar, MessageSquare, History, User, Settings, AlertCircle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Star, Heart, Bell, Check, Clock3, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import ToolCard from '../components/features/ToolCard';
import Dialog from '../components/ui/Dialog';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client';
import { GET_MY_TOOLS } from '../graphql/queries/myTools';
import { useAuth } from '../context/AuthContext';
import { GET_BORROWED_TOOLS } from '../graphql/queries/borrowedTools';
import { CONFIRM_TOOL_BOOKING } from '../graphql/mutations/confirmToolBooking';
import { DECLINE_TOOL_BOOKING } from '../graphql/mutations/declineToolBooking';
import {
  RETURN_TOOL_BOOKING,
  RETURN_TOOL_BOOKING_DIRECT,
  COMPLETE_TOOL_BOOKING,
  COMPLETE_TOOL_BOOKING_DIRECT,
  RETURN_TOOL_BOOKING_WITH_DAMAGES,
} from '../graphql/mutations/returnToolBooking';
import {
  REVIEW_TOOL_BOOKING,
  REVIEW_TOOL_BOOKING_DIRECT,
  COMPLETE_TOOL_BOOKING_WITH_REVIEW,
  COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW,
} from '../graphql/mutations/reviewToolBooking';
import {
  CANCEL_TOOL_BOOKING,
  CANCEL_TOOL_BOOKING_DIRECT,
  PICK_UP_TOOL_BOOKING,
  PICK_UP_TOOL_BOOKING_DIRECT,
} from '../graphql/mutations/borrowToolBookingActions';
import { TOOL_BOOKING_THREAD } from '../graphql/queries/toolBookingThread';
import { GET_MESSAGE_THREADS } from '../graphql/queries/messageThreads';
import { CREATE_MESSAGE_THREAD, CREATE_MESSAGE_THREAD_INPUT } from '../graphql/mutations/createMessageThread';

const GET_LEND_BOOKINGS_BY_STATUS = gql`
  query LendToolBookingsByStatus($limit: Int!, $offset: Int!, $statuses: [String!]!) {
    currentUser {
      lendToolBookings(limit: $limit, offset: $offset, statuses: $statuses) {
        totalCount
        items {
          id
          fromDate
          toDate
          status
          totalPriceCents
          tool {
            id
            name
            primaryImageUrl
            description
          }
          bookingUser {
            id
            name
            avatarImageUrl
            reviewCount
            ratingAverage
          }
        }
      }
    }
  }
`;

const GET_BORROW_BOOKINGS_BY_STATUS = gql`
  query BorrowToolBookingsByStatus($limit: Int!, $offset: Int!, $statuses: [String!]!) {
    currentUser {
      id
      borrowToolBookings(limit: $limit, offset: $offset, statuses: $statuses) {
        totalCount
        items {
          id
          status
          fromDate
          toDate
          totalPriceCents
          tool {
            id
            name
            primaryImageUrl
            description
          }
          bookingUser {
            id
            name
            avatarImageUrl
            reviewCount
            ratingAverage
          }
          owningUser {
            id
            name
            avatarImageUrl
            reviewCount
            ratingAverage
          }
          messageThread {
            id
            subject
          }
        }
      }
    }
  }
`;

const GET_UNREAD_NOTIFICATION_COUNT = gql`
  query UnreadNotificationCount {
    currentUser {
      unreadNotificationCount
    }
  }
`;

const GET_MY_EVENTS = gql`
  query MyEvents($limit: Int, $offset: Int) {
    currentUser {
      domoreEvents(limit: $limit, offset: $offset) {
        totalCount
        items {
          id
          eventType
          eventDatetime
          objectType
          objectId
          eventText
        }
      }
    }
  }
`;

const GET_LEND_STATS = gql`
  query LendStats($activeStatuses: [String!]!, $completedStatuses: [String!]!) {
    myTools(limit: 1, offset: 0) {
      totalCount
    }
    currentUser {
      completedLendBookingTotalCents
      activeLendStats: lendToolBookings(limit: 1, offset: 0, statuses: $activeStatuses) {
        totalCount
      }
      completedLendStats: lendToolBookings(limit: 1, offset: 0, statuses: $completedStatuses) {
        totalCount
      }
    }
  }
`;

const Dashboard: React.FC = () => {
  const ACTIVE_LEND_STATUSES = ['requested', 'confirmed', 'picked_up', 'returned', 'reviewed_by_booking_user'];
  const COMPLETED_LEND_STATUSES = ['reviewed_by_owner', 'completed'];
  const ACTIVE_BORROW_STATUSES = ['requested', 'confirmed', 'picked_up', 'returned', 'reviewed_by_owner'];
  const COMPLETED_BORROW_STATUSES = ['reviewed_by_booking_user', 'completed'];

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as 'tools' | 'lend_bookings' | 'borrow_bookings' | 'messages' | null;
  const [activeTab, setActiveTab] = useState<'tools' | 'lend_bookings' | 'borrow_bookings' | 'messages'>(tabFromUrl || 'tools');
  const [activeLendBookingsPage, setActiveLendBookingsPage] = useState(1);
  const [completedLendBookingsPage, setCompletedLendBookingsPage] = useState(1);
  const [activeBorrowBookingsPage, setActiveBorrowBookingsPage] = useState(1);
  const [completedBorrowBookingsPage, setCompletedBorrowBookingsPage] = useState(1);
  const [messagesPage, setMessagesPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [selectedBookingMode, setSelectedBookingMode] = useState<'lend' | 'borrow'>('lend');
  const [isBookingMessagesCollapsed, setIsBookingMessagesCollapsed] = useState(true);
  const [messageThreadLoadingBookingId, setMessageThreadLoadingBookingId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [returnBooking, setReturnBooking] = useState<any>(null);
  const [returnBookingNote, setReturnBookingNote] = useState('');
  const [returnBookingLoading, setReturnBookingLoading] = useState(false);
  const [returnBookingAction, setReturnBookingAction] = useState<'clean' | 'issues' | null>(null);
  const [showReturnDamageDialog, setShowReturnDamageDialog] = useState(false);
  const [returnDamageComment, setReturnDamageComment] = useState('');
  const [returnDamageFiles, setReturnDamageFiles] = useState<File[]>([]);
  const [reviewBooking, setReviewBooking] = useState<any>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewBookingLoading, setReviewBookingLoading] = useState(false);
  const [borrowActionDialog, setBorrowActionDialog] = useState<{ booking: any; action: 'cancel' | 'pickup' } | null>(null);
  const [borrowActionLoading, setBorrowActionLoading] = useState(false);
  const [borrowActionLoadingBookingId, setBorrowActionLoadingBookingId] = useState<string | null>(null);
  const [borrowReviewBooking, setBorrowReviewBooking] = useState<any>(null);
  const [borrowReviewRating, setBorrowReviewRating] = useState(0);
  const [borrowReviewComment, setBorrowReviewComment] = useState('');
  const [borrowReviewLoading, setBorrowReviewLoading] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const lendBookingsRef = useRef<HTMLDivElement>(null);
  const borrowBookingsRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    if (!loading && !user) {
      navigate('/signin');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const tab = searchParams.get('tab') as 'tools' | 'lend_bookings' | 'borrow_bookings' | 'messages' | null;
    if (tab && ['tools', 'lend_bookings', 'borrow_bookings', 'messages'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Scroll til aktiv tab-sektion når tab vælges
  useEffect(() => {
    const refs = {
      tools: toolsRef,
      lend_bookings: lendBookingsRef,
      borrow_bookings: borrowBookingsRef,
      messages: messagesRef
    };
    
    const currentRef = refs[activeTab];
    if (currentRef?.current) {
      // Lille delay for at sikre DOM er renderet
      setTimeout(() => {
        currentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [activeTab]);

  const handleTabChange = (tab: 'tools' | 'lend_bookings' | 'borrow_bookings' | 'messages') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Helper function to get status badge styling, text and icon
  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { text: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
      requested: { text: 'Anmodet', color: 'bg-yellow-100 text-yellow-800', icon: Clock3 },
      declined: { text: 'Afvist', color: 'bg-red-100 text-red-800', icon: X },
      canceled: { text: 'Annulleret', color: 'bg-gray-100 text-gray-800', icon: X },
      confirmed: { text: 'Bekræftet', color: 'bg-blue-100 text-blue-800', icon: Check },
      picked_up: { text: 'Afhentet', color: 'bg-green-100 text-green-800', icon: Check },
      returned: { text: 'Returneret', color: 'bg-purple-100 text-purple-800', icon: Check },
      reviewed_by_booking_user: { text: 'Anmeldt af lejer', color: 'bg-indigo-100 text-indigo-800', icon: Star },
      reviewed_by_owner: { text: 'Anmeldt af ejer', color: 'bg-indigo-100 text-indigo-800', icon: Star },
      completed: { text: 'Afsluttet', color: 'bg-gray-100 text-gray-800', icon: Check },
    };
    return statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
  };

  const renderStatusBadge = (status: string) => {
    const statusBadge = getStatusBadge(status);
    const StatusIcon = statusBadge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
        <StatusIcon className="h-3.5 w-3.5" />
        <span>{statusBadge.text}</span>
      </span>
    );
  };
  
  const { data: myToolsData } = useQuery(GET_MY_TOOLS, { 
    skip: !user, 
    fetchPolicy: "no-cache",
    variables: {
      date: new Date().toISOString().split('T')[0]
    }
  });
  const userTools: any[] = myToolsData?.myTools?.items || [];
  const { data: borrowedData } = useQuery(GET_BORROWED_TOOLS, { 
    skip: !user, 
    fetchPolicy: "no-cache",
    variables: {
      date: new Date().toISOString().split('T')[0]
    }
  });
  const borrowedTools: any[] = borrowedData?.borrowedTools?.items || [];

  const { data: unreadNotificationsData } = useQuery(GET_UNREAD_NOTIFICATION_COUNT, {
    skip: !user,
    fetchPolicy: 'network-only',
  });
  const unreadNotificationCount = unreadNotificationsData?.currentUser?.unreadNotificationCount || 0;

  const { data: lendStatsData, refetch: refetchLendStats } = useQuery(GET_LEND_STATS, {
    skip: !user,
    variables: {
      activeStatuses: ACTIVE_LEND_STATUSES,
      completedStatuses: COMPLETED_LEND_STATUSES,
    },
    fetchPolicy: 'network-only',
  });

  // Fetch active bookings with pagination
  const { data: activeBookingsData, loading: activeBookingsLoading, refetch: refetchActiveLendBookings } = useQuery(GET_LEND_BOOKINGS_BY_STATUS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (activeLendBookingsPage - 1) * ITEMS_PER_PAGE,
      statuses: ACTIVE_LEND_STATUSES,
    },
    fetchPolicy: 'network-only',
  });

  // Fetch completed bookings with pagination
  const { data: completedBookingsData, loading: completedBookingsLoading, refetch: refetchCompletedLendBookings } = useQuery(GET_LEND_BOOKINGS_BY_STATUS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (completedLendBookingsPage - 1) * ITEMS_PER_PAGE,
      statuses: COMPLETED_LEND_STATUSES,
    },
    fetchPolicy: 'network-only',
  });

  const activeLendBookings = activeBookingsData?.currentUser?.lendToolBookings?.items || [];
  const activeLendBookingsTotal = activeBookingsData?.currentUser?.lendToolBookings?.totalCount || 0;
  const activeLendBookingsTotalPages = Math.ceil(activeLendBookingsTotal / ITEMS_PER_PAGE);

  const completedLendBookings = completedBookingsData?.currentUser?.lendToolBookings?.items || [];
  const completedLendBookingsTotal = completedBookingsData?.currentUser?.lendToolBookings?.totalCount || 0;
  const completedLendBookingsTotalPages = Math.ceil(completedLendBookingsTotal / ITEMS_PER_PAGE);

  const lendStatsMyToolsTotal = lendStatsData?.myTools?.totalCount ?? myToolsData?.myTools?.totalCount ?? userTools.length;
  const lendStatsActiveTotal = lendStatsData?.currentUser?.activeLendStats?.totalCount ?? activeLendBookingsTotal;
  const lendStatsCompletedTotal = lendStatsData?.currentUser?.completedLendStats?.totalCount ?? completedLendBookingsTotal;
  const lendStatsCompletedTotalCents = lendStatsData?.currentUser?.completedLendBookingTotalCents ?? user?.completedLendBookingTotalCents;

  const { data: activeBorrowBookingsData, loading: activeBorrowBookingsLoading, refetch: refetchActiveBorrowBookings } = useQuery(GET_BORROW_BOOKINGS_BY_STATUS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (activeBorrowBookingsPage - 1) * ITEMS_PER_PAGE,
      statuses: ACTIVE_BORROW_STATUSES,
    },
    fetchPolicy: 'network-only',
  });

  const { data: completedBorrowBookingsData, loading: completedBorrowBookingsLoading, refetch: refetchCompletedBorrowBookings } = useQuery(GET_BORROW_BOOKINGS_BY_STATUS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (completedBorrowBookingsPage - 1) * ITEMS_PER_PAGE,
      statuses: COMPLETED_BORROW_STATUSES,
    },
    fetchPolicy: 'network-only',
  });

  const activeBorrowBookings = activeBorrowBookingsData?.currentUser?.borrowToolBookings?.items || [];
  const activeBorrowBookingsTotal = activeBorrowBookingsData?.currentUser?.borrowToolBookings?.totalCount || 0;
  const activeBorrowBookingsTotalPages = Math.ceil(activeBorrowBookingsTotal / ITEMS_PER_PAGE);

  const completedBorrowBookings = completedBorrowBookingsData?.currentUser?.borrowToolBookings?.items || [];
  const completedBorrowBookingsTotal = completedBorrowBookingsData?.currentUser?.borrowToolBookings?.totalCount || 0;
  const completedBorrowBookingsTotalPages = Math.ceil(completedBorrowBookingsTotal / ITEMS_PER_PAGE);

  // Fetch message threads with pagination
  const { data: messagesData, loading: messagesLoading } = useQuery(GET_MESSAGE_THREADS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (messagesPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const messageThreads = messagesData?.currentUser?.messageThreads?.items || [];
  const messageThreadsTotal = messagesData?.currentUser?.messageThreads?.totalCount || 0;
  const messageThreadsTotalPages = Math.ceil(messageThreadsTotal / ITEMS_PER_PAGE);

  const { data: eventsData, loading: eventsLoading } = useQuery(GET_MY_EVENTS, {
    skip: !user,
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (eventsPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const events = eventsData?.currentUser?.domoreEvents?.items || [];
  const eventsTotal = eventsData?.currentUser?.domoreEvents?.totalCount || 0;
  const eventsTotalPages = Math.ceil(eventsTotal / ITEMS_PER_PAGE);
  const currentUserAvatarSrc = user?.avatarImageUrl || user?.avatar;

  // Fetch message thread for selected booking
  const { data: threadData, loading: threadLoading } = useQuery(TOOL_BOOKING_THREAD, {
    variables: { id: selectedBooking?.id },
    skip: !selectedBooking?.id,
    fetchPolicy: 'network-only',
  });
  const [fetchBookingThread] = useLazyQuery(TOOL_BOOKING_THREAD, {
    fetchPolicy: 'network-only',
  });
  const [createMessageThread] = useMutation(CREATE_MESSAGE_THREAD);
  const [createMessageThreadInput] = useMutation(CREATE_MESSAGE_THREAD_INPUT);
  const [returnToolBooking] = useMutation(RETURN_TOOL_BOOKING);
  const [returnToolBookingDirect] = useMutation(RETURN_TOOL_BOOKING_DIRECT);
  const [returnToolBookingWithDamages] = useMutation(RETURN_TOOL_BOOKING_WITH_DAMAGES);
  const [completeToolBooking] = useMutation(COMPLETE_TOOL_BOOKING);
  const [completeToolBookingDirect] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT);
  const [reviewToolBooking] = useMutation(REVIEW_TOOL_BOOKING);
  const [reviewToolBookingDirect] = useMutation(REVIEW_TOOL_BOOKING_DIRECT);
  const [completeToolBookingWithReview] = useMutation(COMPLETE_TOOL_BOOKING_WITH_REVIEW);
  const [completeToolBookingDirectWithReview] = useMutation(COMPLETE_TOOL_BOOKING_DIRECT_WITH_REVIEW);
  const [cancelToolBooking] = useMutation(CANCEL_TOOL_BOOKING);
  const [cancelToolBookingDirect] = useMutation(CANCEL_TOOL_BOOKING_DIRECT);
  const [pickUpToolBooking] = useMutation(PICK_UP_TOOL_BOOKING);
  const [pickUpToolBookingDirect] = useMutation(PICK_UP_TOOL_BOOKING_DIRECT);

  const bookingMessages = threadData?.toolBooking?.messageThread?.messages || [];

  // Mutations
  const [confirmToolBooking, { loading: confirmLoading }] = useMutation(CONFIRM_TOOL_BOOKING, {
    onCompleted: (data) => {
      if (data?.confirmToolBooking?.errors && data.confirmToolBooking.errors.length > 0) {
        toast.error(`Fejl: ${data.confirmToolBooking.errors.join(', ')}`);
      } else {
        toast.success('Booking bekræftet!');
        setShowConfirmDialog(false);
        setSelectedBooking(null);
        void Promise.all([
          refetchActiveLendBookings(),
          refetchCompletedLendBookings(),
          refetchLendStats(),
        ]);
      }
    },
    onError: (error) => {
      toast.error(`Fejl ved bekræftelse: ${error.message}`);
    },
  });

  const [declineToolBooking, { loading: declineLoading }] = useMutation(DECLINE_TOOL_BOOKING, {
    onCompleted: (data) => {
      if (data?.declineToolBooking?.errors && data.declineToolBooking.errors.length > 0) {
        toast.error(`Fejl: ${data.declineToolBooking.errors.join(', ')}`);
      } else {
        toast.success('Booking afvist!');
        setShowDeclineDialog(false);
        setSelectedBooking(null);
        void Promise.all([
          refetchActiveLendBookings(),
          refetchCompletedLendBookings(),
          refetchLendStats(),
        ]);
      }
    },
    onError: (error) => {
      toast.error(`Fejl ved afvisning: ${error.message}`);
    },
  });

  // Format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(['da-DK'], options);
  };

  const getEventIconConfig = (eventType: string) => {
    const normalizedType = (eventType || '').toLowerCase();

    if (normalizedType.includes('message')) {
      return {
        icon: MessageSquare,
        containerClass: 'bg-green-100 text-green-600',
      };
    }

    if (normalizedType.includes('return')) {
      return {
        icon: Hammer,
        containerClass: 'bg-purple-100 text-purple-600',
      };
    }

    if (normalizedType.includes('request')) {
      return {
        icon: AlertCircle,
        containerClass: 'bg-orange-100 text-orange-600',
      };
    }

    if (normalizedType.includes('booking') || normalizedType.includes('book')) {
      return {
        icon: Calendar,
        containerClass: 'bg-blue-100 text-blue-600',
      };
    }

    return {
      icon: Bell,
      containerClass: 'bg-gray-100 text-gray-600',
    };
  };

  // Format price from cents to Danish format (only show decimals if needed)
  const formatPrice = (cents: number) => {
    const kroner = cents / 100;
    const hasDecimals = cents % 100 !== 0;
    return `${kroner.toLocaleString('da-DK', { 
      minimumFractionDigits: hasDecimals ? 2 : 0, 
      maximumFractionDigits: 2 
    })} kr`;
  };

  const getLendBookingAction = (status: string) => {
    if (status === 'requested') {
      return {
        label: 'Håndtér',
        variant: 'primary' as const,
        action: 'details' as const,
        icon: <Clock3 className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'picked_up') {
      return {
        label: 'Bekræft retur',
        variant: 'primary' as const,
        action: 'return' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'returned' || status === 'reviewed_by_booking_user') {
      return {
        label: 'Afslut',
        variant: 'primary' as const,
        action: 'review' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    return {
      label: 'Se status',
      variant: 'outline' as const,
      action: 'details' as const,
      icon: undefined,
    };
  };

  const shouldHideLendActionButton = (status: string) =>
    status === 'confirmed' ||
    status === 'canceled' ||
    status === 'declined' ||
    status === 'completed' ||
    status === 'reviewed_by_owner';

  const getLendBadgeStatus = (status: string) => {
    if (status === 'reviewed_by_booking_user') return 'returned';
    if (status === 'reviewed_by_owner') return 'completed';
    return status;
  };

  const getBorrowBadgeStatus = (status: string) => {
    if (status === 'reviewed_by_booking_user') return 'completed';
    if (status === 'reviewed_by_owner') return 'returned';
    return status;
  };

  const getBorrowBookingAction = (status: string) => {
    if (status === 'requested') {
      return {
        label: 'Annuller',
        variant: 'outline' as const,
        action: 'cancel' as const,
        icon: <X className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'confirmed') {
      return {
        label: 'Afhentet',
        variant: 'primary' as const,
        action: 'pickup' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    if (status === 'returned' || status === 'reviewed_by_owner') {
      return {
        label: 'Afslut',
        variant: 'primary' as const,
        action: 'complete' as const,
        icon: <Check className="h-4 w-4 mr-2" />,
      };
    }

    return null;
  };

  const getDayDifferenceFromToday = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);

    return Math.round((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getBorrowReminders = (booking: any) => {
    if (!booking?.fromDate || !booking?.toDate) return [];

    const hiddenStatuses = ['declined', 'canceled', 'completed', 'reviewed_by_booking_user'];
    if (hiddenStatuses.includes(booking.status)) return [];

    const reminders: Array<{ key: 'pickup' | 'return' | 'overdue'; title: string; message: string }> = [];

    const fromDayDiff = getDayDifferenceFromToday(booking.fromDate);
    const toDayDiff = getDayDifferenceFromToday(booking.toDate);

    if ((booking.status === 'requested' || booking.status === 'confirmed') && (fromDayDiff === 0 || fromDayDiff === 1)) {
      reminders.push({
        key: 'pickup',
        title: 'Afhentningspåmindelse',
        message: fromDayDiff === 0 ? 'Husk at afhente værktøjet i dag.' : 'Husk at afhente værktøjet i morgen.',
      });
    }

    if (booking.status === 'picked_up' && (toDayDiff === 0 || toDayDiff === 1)) {
      reminders.push({
        key: 'return',
        title: 'Returneringspåmindelse',
        message: toDayDiff === 0 ? 'Husk at returnere værktøjet i dag.' : 'Husk at returnere værktøjet i morgen.',
      });
    }

    if (booking.status === 'picked_up' && toDayDiff < 0) {
      reminders.push({
        key: 'overdue',
        title: 'Returnering overskredet',
        message: 'Returneringsdatoen er overskredet. Husk at returnere værktøjet hurtigst muligt.',
      });
    }

    return reminders;
  };

  const getBookingCounterparty = (booking: any) => {
    if (!booking) return null;
    if (booking?.bookingUser?.id === user?.id) return booking?.owningUser || null;
    if (booking?.owningUser?.id === user?.id) return booking?.bookingUser || null;
    return booking?.bookingUser || booking?.owningUser || null;
  };

  // Handle opening booking dialog
  const handleOpenBookingDialog = (booking: any, mode: 'lend' | 'borrow' = 'lend') => {
    setSelectedBookingMode(mode);
    setIsBookingMessagesCollapsed(true);
    setSelectedBooking(booking);
  };

  const handleOpenReturnDialog = (booking: any) => {
    setReturnBooking(booking);
    setReturnBookingNote('');
    setReturnBookingAction(null);
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleCloseReturnDialog = () => {
    if (returnBookingLoading) return;
    setReturnBooking(null);
    setReturnBookingNote('');
    setReturnBookingAction(null);
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleOpenReturnDamageDialog = () => {
    setShowReturnDamageDialog(true);
    setReturnDamageComment(returnBookingNote.trim());
    setReturnDamageFiles([]);
  };

  const handleCloseReturnDamageDialog = () => {
    if (returnBookingLoading) return;
    setShowReturnDamageDialog(false);
    setReturnDamageComment('');
    setReturnDamageFiles([]);
  };

  const handleReturnDamageFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    if (imageFiles.length !== files.length) {
      toast.error('Kun billeder kan uploades i skadeanmeldelsen.');
    }

    if (imageFiles.length > 0) {
      setReturnDamageFiles((prev) => [...prev, ...imageFiles]);
    }

    event.target.value = '';
  };

  const handleRemoveReturnDamageFile = (index: number) => {
    setReturnDamageFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const convertFileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result) {
          reject(new Error('Kunne ikke læse filen.'));
          return;
        }
        resolve(result.split(',')[1]);
      };
      reader.onerror = () => reject(new Error('Kunne ikke læse filen.'));
      reader.readAsDataURL(file);
    });

  const handleSubmitReturnedWithDamages = async () => {
    if (!returnBooking?.id) return;

    const bookingToReview = returnBooking;

    const trimmedComment = returnDamageComment.trim();
    if (!trimmedComment) {
      toast.error('Skriv venligst en kommentar om skaden eller manglen.');
      return;
    }

    if (returnDamageFiles.length === 0) {
      toast.error('Upload mindst ét billede af skaden eller manglen.');
      return;
    }

    setReturnBookingLoading(true);
    setReturnBookingAction('issues');

    try {
      const convertedAttachments = await Promise.all(
        returnDamageFiles.map(async (file) => ({
          filename: file.name,
          contentType: file.type,
          base64: await convertFileToBase64(file),
        }))
      );

      const { data } = await returnToolBookingWithDamages({
        variables: {
          id: returnBooking.id,
          comment: trimmedComment,
          attachments: convertedAttachments,
        },
      });

      const payload = data?.toolReturnedWithDamages;
      const returnErrors = payload?.errors || [];
      if (returnErrors.length > 0) {
        toast.error(returnErrors[0]);
        return;
      }

      toast.success('Skadeanmeldelse sendt. Vores support vender tilbage hurtigst muligt.');
      setReturnBooking(null);
      setReturnBookingNote('');
      setReturnDamageComment('');
      setReturnDamageFiles([]);
      setShowReturnDamageDialog(false);
      setReturnBookingAction(null);
      setSelectedBooking(null);
      handleOpenReviewDialog(bookingToReview);
      await Promise.all([
        refetchActiveLendBookings(),
        refetchCompletedLendBookings(),
        refetchLendStats(),
      ]);
    } catch {
      toast.error('Der opstod en fejl ved registrering af skadeanmeldelsen.');
    } finally {
      setReturnBookingLoading(false);
      setReturnBookingAction(null);
    }
  };

  const resetReviewDialogState = () => {
    setReviewBooking(null);
    setReviewRating(0);
    setReviewComment('');
  };

  const handleOpenReviewDialog = (booking: any) => {
    setReviewBooking(booking);
    setReviewRating(0);
    setReviewComment('');
  };

  const handleCloseReviewDialog = () => {
    if (reviewBookingLoading) return;
    resetReviewDialogState();
  };

  const handleOpenBookingConversation = async (booking: any) => {
    if (!booking?.id) return;

    setMessageThreadLoadingBookingId(booking.id);

    try {
      if (booking?.messageThread?.id) {
        navigate(`/conversation/${booking.messageThread.id}`);
        return;
      }

      const { data } = await fetchBookingThread({
        variables: { id: booking.id },
      });

      const threadId = data?.toolBooking?.messageThread?.id;
      if (threadId) {
        navigate(`/conversation/${threadId}`);
        return;
      }

      const toolId = booking?.tool?.id;
      if (!toolId) {
        toast.error('Kunne ikke finde værktøjet for beskeden.');
        return;
      }

      const counterparty = getBookingCounterparty(booking);
      const participantIds = counterparty?.id ? [counterparty.id] : undefined;
      const variables = {
        toolId,
        toolBookingId: booking.id,
        participantIds,
      };

      const isSchemaArgumentError = (error: any) => {
        const graphQLErrors = error?.graphQLErrors || [];
        return graphQLErrors.some((graphQLError: any) => {
          const code = graphQLError?.extensions?.code;
          return code === 'missingRequiredArguments' || code === 'argumentNotAccepted' || code === 'variableNotUsed';
        });
      };

      let toolThreadData: any;
      try {
        const response = await createMessageThread({ variables });
        toolThreadData = response.data;
      } catch (createThreadError) {
        if (!isSchemaArgumentError(createThreadError)) {
          throw createThreadError;
        }

        const fallbackResponse = await createMessageThreadInput({ variables });
        toolThreadData = fallbackResponse.data;
      }

      const toolThreadErrors = toolThreadData?.createMessageThread?.errors || [];
      if (toolThreadErrors.length > 0) {
        toast.error(toolThreadErrors[0]);
        return;
      }

      const fallbackThreadId = toolThreadData?.createMessageThread?.messageThread?.id;
      if (!fallbackThreadId) {
        toast.error('Kunne ikke oprette en ny beskedtråd.');
        return;
      }

      navigate(`/conversation/${fallbackThreadId}`);
    } catch {
      toast.error('Der opstod en fejl ved åbning af beskedtråden.');
    } finally {
      setMessageThreadLoadingBookingId(null);
    }
  };

  const bookingDialogCounterparty = selectedBookingMode === 'borrow'
    ? selectedBooking?.owningUser
    : selectedBooking?.bookingUser;

  // Handle confirm booking
  const handleConfirmBooking = async (message: string) => {
    if (!selectedBooking?.id) return;
    
    await confirmToolBooking({
      variables: {
        id: selectedBooking.id,
        message: message || null,
      },
    });
  };

  // Handle decline booking
  const handleDeclineBooking = async (message: string) => {
    if (!selectedBooking?.id) return;
    
    await declineToolBooking({
      variables: {
        id: selectedBooking.id,
        message: message || null,
      },
    });
  };

  const handleOpenBorrowActionDialog = (booking: any, action: 'cancel' | 'pickup') => {
    setBorrowActionDialog({ booking, action });
  };

  const handleCloseBorrowActionDialog = () => {
    if (borrowActionLoading) return;
    setBorrowActionDialog(null);
  };

  const resetBorrowReviewDialogState = () => {
    setBorrowReviewBooking(null);
    setBorrowReviewRating(0);
    setBorrowReviewComment('');
  };

  const handleOpenBorrowReviewDialog = (booking: any) => {
    setBorrowReviewBooking(booking);
    setBorrowReviewRating(0);
    setBorrowReviewComment('');
  };

  const handleCloseBorrowReviewDialog = () => {
    if (borrowReviewLoading) return;
    resetBorrowReviewDialogState();
  };

  const shouldTryNextBorrowActionMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const handleConfirmBorrowAction = async (message: string) => {
    const booking = borrowActionDialog?.booking;
    const action = borrowActionDialog?.action;
    if (!booking?.id || !action) return;

    setBorrowActionLoading(true);
    setBorrowActionLoadingBookingId(booking.id);

    const variables = {
      id: booking.id,
      message: message?.trim() || null,
    };

    try {
      const getCancelPayload = (data: any) => data?.cancelToolBooking;
      const getPickupPayload = (data: any) => data?.pickUpToolBooking;

      const attempts =
        action === 'cancel'
          ? [
              () => cancelToolBooking({ variables }),
              () => cancelToolBookingDirect({ variables }),
            ]
          : [
              () => pickUpToolBooking({ variables }),
              () => pickUpToolBookingDirect({ variables }),
            ];

      const getPayload = action === 'cancel' ? getCancelPayload : getPickupPayload;

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextBorrowActionMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel mutation for handlingen.');
      }

      const actionErrors = payload?.errors || [];
      if (actionErrors.length > 0) {
        toast.error(actionErrors[0]);
        return;
      }

      toast.success(action === 'cancel' ? 'Låneanmodningen er annulleret.' : 'Lånet er markeret som afhentet.');
      setBorrowActionDialog(null);
      setSelectedBooking(null);
      await Promise.all([
        refetchActiveBorrowBookings(),
        refetchCompletedBorrowBookings(),
      ]);
    } catch {
      toast.error(action === 'cancel' ? 'Der opstod en fejl ved annullering af lånet.' : 'Der opstod en fejl ved markering af afhentning.');
    } finally {
      setBorrowActionLoading(false);
      setBorrowActionLoadingBookingId(null);
    }
  };

  const handleCompleteBorrowWithoutReview = async () => {
    if (!borrowReviewBooking?.id) return;

    setBorrowReviewLoading(true);

    const note = borrowReviewComment.trim();
    const message = note
      ? `Lån afsluttet uden bedømmelse. ${note}`
      : 'Lån afsluttet uden bedømmelse.';
    const variables = {
      id: borrowReviewBooking.id,
      message,
    };

    try {
      const attempts = [
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = response?.data?.completeToolBooking;
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel afslutnings-mutation.');
      }

      const completeErrors = payload?.errors || [];
      if (completeErrors.length > 0) {
        toast.error(completeErrors[0]);
        return;
      }

      toast.success('Lånet er afsluttet.');
      resetBorrowReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afslutning af lånet.');
    } finally {
      setBorrowReviewLoading(false);
    }
  };

  const handleSubmitBorrowReview = async () => {
    if (!borrowReviewBooking?.id) return;
    if (borrowReviewRating < 1) {
      await handleCompleteBorrowWithoutReview();
      return;
    }

    setBorrowReviewLoading(true);

    const comment = borrowReviewComment.trim() || null;
    const reviewVariables = {
      id: borrowReviewBooking.id,
      rating: borrowReviewRating,
      comment,
    };
    const reviewAndCompleteVariables = {
      id: borrowReviewBooking.id,
      rating: borrowReviewRating,
      comment,
      message: 'Lån afsluttet med bedømmelse.',
    };
    const completeVariables = {
      id: borrowReviewBooking.id,
      message: comment
        ? `Lån afsluttet med bedømmelse. ${comment}`
        : 'Lån afsluttet med bedømmelse.',
    };

    const getPayload = (data: any) => data?.reviewToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => reviewToolBooking({ variables: reviewVariables }),
        () => reviewToolBookingDirect({ variables: reviewVariables }),
        () => completeToolBookingWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBookingDirectWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBooking({ variables: completeVariables }),
        () => completeToolBookingDirect({ variables: completeVariables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel bedømmelses-mutation.');
      }

      const reviewErrors = payload?.errors || [];
      if (reviewErrors.length > 0) {
        toast.error(reviewErrors[0]);
        return;
      }

      toast.success('Bedømmelse gemt og lånet er afsluttet.');
      resetBorrowReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afsendelse af bedømmelsen.');
    } finally {
      setBorrowReviewLoading(false);
    }
  };

  const shouldTryNextReturnMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const shouldTryNextReviewMutation = (error: any) => {
    const graphQLErrors = error?.graphQLErrors || [];
    if (graphQLErrors.length === 0) return false;

    return graphQLErrors.every((graphQLError: any) => {
      const code = graphQLError?.extensions?.code;
      const message = (graphQLError?.message || '').toLowerCase();

      return (
        code === 'undefinedField' ||
        code === 'missingRequiredArguments' ||
        code === 'argumentNotAccepted' ||
        code === 'variableNotUsed' ||
        message.includes("doesn't exist") ||
        message.includes('unknown argument') ||
        message.includes('cannot query field')
      );
    });
  };

  const handleCompleteWithoutReview = async () => {
    if (!reviewBooking?.id) return;

    setReviewBookingLoading(true);

    const note = reviewComment.trim();
    const message = note
      ? `Booking afsluttet uden bedømmelse. ${note}`
      : 'Booking afsluttet uden bedømmelse.';
    const variables = {
      id: reviewBooking.id,
      message,
    };

    try {
      const attempts = [
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = response?.data?.completeToolBooking;
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel afslutnings-mutation.');
      }

      const completeErrors = payload?.errors || [];
      if (completeErrors.length > 0) {
        toast.error(completeErrors[0]);
        return;
      }

      toast.success('Booking afsluttet uden bedømmelse.');
      resetReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afslutning af booking.');
    } finally {
      setReviewBookingLoading(false);
    }
  };

  const handleSubmitOwnerReview = async () => {
    if (!reviewBooking?.id) return;
    if (reviewRating < 1) {
      await handleCompleteWithoutReview();
      return;
    }

    setReviewBookingLoading(true);

    const comment = reviewComment.trim() || null;
    const reviewVariables = {
      id: reviewBooking.id,
      rating: reviewRating,
      comment,
    };
    const reviewAndCompleteVariables = {
      id: reviewBooking.id,
      rating: reviewRating,
      comment,
      message: 'Booking afsluttet med bedømmelse.',
    };
    const completeVariables = {
      id: reviewBooking.id,
      message: comment
        ? `Booking afsluttet med bedømmelse. ${comment}`
        : 'Booking afsluttet med bedømmelse.',
    };

    const getPayload = (data: any) => data?.reviewToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => reviewToolBooking({ variables: reviewVariables }),
        () => reviewToolBookingDirect({ variables: reviewVariables }),
        () => completeToolBookingWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBookingDirectWithReview({ variables: reviewAndCompleteVariables }),
        () => completeToolBooking({ variables: completeVariables }),
        () => completeToolBookingDirect({ variables: completeVariables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReviewMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel bedømmelses-mutation.');
      }

      const reviewErrors = payload?.errors || [];
      if (reviewErrors.length > 0) {
        toast.error(reviewErrors[0]);
        return;
      }

      toast.success('Bedømmelse gemt og booking afsluttet.');
      resetReviewDialogState();
      setSelectedBooking(null);
      window.location.reload();
    } catch {
      toast.error('Der opstod en fejl ved afsendelse af bedømmelsen.');
    } finally {
      setReviewBookingLoading(false);
    }
  };

  const handleMarkReturned = async (hasIssues: boolean) => {
    if (!returnBooking?.id) return;

    const bookingToReview = returnBooking;

    setReturnBookingLoading(true);
    setReturnBookingAction(hasIssues ? 'issues' : 'clean');

    const baseMessage = hasIssues
      ? 'Værktøjet er returneret med skader eller mangler.'
      : 'Værktøjet er returneret.';

    const detailsMessage = returnBookingNote.trim();
    const message = detailsMessage ? `${baseMessage} ${detailsMessage}` : baseMessage;
    const variables = {
      id: returnBooking.id,
      message,
    };

    const getPayload = (data: any) => data?.returnToolBooking || data?.completeToolBooking;

    try {
      const attempts = [
        () => returnToolBooking({ variables }),
        () => returnToolBookingDirect({ variables }),
        () => completeToolBooking({ variables }),
        () => completeToolBookingDirect({ variables }),
      ];

      let payload: any = null;
      let lastSchemaError: any = null;

      for (const attempt of attempts) {
        try {
          const response = await attempt();
          payload = getPayload(response?.data);
          if (payload) break;
        } catch (error) {
          if (shouldTryNextReturnMutation(error)) {
            lastSchemaError = error;
            continue;
          }
          throw error;
        }
      }

      if (!payload) {
        throw lastSchemaError || new Error('Kunne ikke finde en kompatibel returnerings-mutation.');
      }

      const returnErrors = payload?.errors || [];
      if (returnErrors.length > 0) {
        toast.error(returnErrors[0]);
        return;
      }

      toast.success(hasIssues ? 'Returnering med skader/mangler registreret.' : 'Værktøjet er markeret som returneret.');
      setReturnBooking(null);
      setReturnBookingNote('');
      setReturnBookingAction(null);
      setSelectedBooking(null);
      handleOpenReviewDialog(bookingToReview);
      await Promise.all([
        refetchActiveLendBookings(),
        refetchCompletedLendBookings(),
        refetchLendStats(),
      ]);
    } catch {
      toast.error('Der opstod en fejl ved registrering af returnering.');
    } finally {
      setReturnBookingLoading(false);
      setReturnBookingAction(null);
    }
  };

  
  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* User Profile Summary */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="h-14 w-14 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium overflow-hidden">
                  {currentUserAvatarSrc ? (
                    <img src={currentUserAvatarSrc} alt={user?.name} className="h-full w-full object-cover" />
                  ) : (
                    <span>{user?.name?.charAt(0).toUpperCase() || "U"}</span>
                  )}
                </div>
                <div className="ml-4">
                  <h2 className="text-lg font-medium text-gray-900">{user?.name}</h2>
                  <p className="text-sm text-gray-500">Medlem siden {formatDate(user?.createdAt)}</p>
                </div>
              </div>
              <div className="mt-4">
                <Link to={user?.id ? `/profile/${user.id}` : '/profile'}>
                  <Button variant="outline" size="sm" fullWidth icon={<User className="h-4 w-4 mr-2" />}>
                    Se profil
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="p-4">
              <div className="space-y-1">
                <button
                  onClick={() => handleTabChange('tools')}
                  className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'tools'
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Hammer className="h-5 w-5 mr-3" />
                  <span>Mit værktøj</span>
                </button>
                
                <button
                  onClick={() => handleTabChange('lend_bookings')}
                  className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'lend_bookings'
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="h-5 w-5 mr-3" />
                  <span>Udlån</span>
                  {user?.lendToolBookingActionCount > 0 && (
                    <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {user.lendToolBookingActionCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => handleTabChange('borrow_bookings')}
                  className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'borrow_bookings'
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="h-5 w-5 mr-3" />
                  <span>Lån</span>
                  {user?.borrowToolBookingActionCount > 0 && (
                    <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {user.borrowToolBookingActionCount}
                    </span>
                  )}
                </button>
                
                
                <button
                  onClick={() => handleTabChange('messages')}
                  className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'messages'
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <MessageSquare className="h-5 w-5 mr-3" />
                  <span>Beskeder</span>
                  {user?.unreadMessageCount > 0 && (
                    <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {user.unreadMessageCount}
                    </span>
                  )}
                </button>
                
                <Link
                  to="/favorites"
                  className="flex items-center w-full px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  <Heart className="h-5 w-5 mr-3" />
                  <span>Favoritter</span>
                </Link>

                <Link
                  to="/notifications"
                  className="flex items-center w-full px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  <Bell className="h-5 w-5 mr-3" />
                  <span>Notifikationer</span>
                  {unreadNotificationCount > 0 && (
                    <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                      {unreadNotificationCount}
                    </span>
                  )}
                </Link>
                
                <Link
                  to="/history"
                  className="flex items-center w-full px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  <History className="h-5 w-5 mr-3" />
                  <span>Historik</span>
                </Link>
                
                <Link
                  to="/settings"
                  className="flex items-center w-full px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="h-5 w-5 mr-3" />
                  <span>Indstillinger</span>
                </Link>
              </div>
            </nav>
          </div>
          
          {/* Stats Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-6">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Din udlejningsstatistik</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Dit værktøj</p>
                  <p className="text-2xl font-bold text-gray-900">{lendStatsMyToolsTotal}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Aktive</p>
                  <p className="text-2xl font-bold text-gray-900">{lendStatsActiveTotal}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Afsluttet</p>
                  <p className="text-2xl font-bold text-gray-900">{lendStatsCompletedTotal}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Indtjening</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {lendStatsCompletedTotalCents ? formatPrice(lendStatsCompletedTotalCents) : '0 kr'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* My Tools Tab */}
          {activeTab === 'tools' && (
            <div ref={toolsRef}>
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Mit værktøj</h1>
                <Link to="/add-tool">
                  <Button variant="primary" icon={<PlusCircle className="h-4 w-4 mr-2" />}>
                    Tilføj værktøj
                  </Button>
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Dit værktøj</h2>
                  <Link to="/manage-tools" className="text-sm text-green-600 font-medium">
                    Administrér værktøj
                  </Link>
                </div>
                
                {userTools.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {userTools.map((tool) => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 text-gray-400">
                      <Hammer className="h-12 w-12" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Intet værktøj endnu</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Kom i gang ved at tilføje dit første værktøj.
                    </p>
                    <div className="mt-6">
                      <Link to="/add-tool">
                        <Button variant="primary" icon={<PlusCircle className="h-4 w-4 mr-2" />}>
                          Tilføj værktøj
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Værktøj du låner</h2>
                  <Link to="/borrowed-tools" className="text-sm text-green-600 font-medium">
                    Se alt
                  </Link>
                </div>
                
                {borrowedTools.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {borrowedTools.map((tool) => (
                      <ToolCard key={tool.id} tool={tool} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto h-12 w-12 text-gray-400">
                      <AlertCircle className="h-12 w-12" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Du låner intet værktøj</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Se tilgængeligt værktøj for at finde hvad du har brug for.
                    </p>
                    <div className="mt-6">
                      <Link to="/browse">
                        <Button variant="outline">Find værktøj</Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Lend Bookings Tab */}
          {activeTab === 'lend_bookings' && (
            <div ref={lendBookingsRef}>
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Udlån</h1>
                <Link to="/all-lend-bookings">
                  <Button variant="outline">Se alle udlån</Button>
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Aktive & kommende udlån</h2>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {activeLendBookingsTotal}
                  </span>
                </div>
                
                {activeBookingsLoading ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500">Indlæser...</p>
                  </div>
                ) : activeLendBookings.length > 0 ? (
                  <>
                    <div className="space-y-4">
                      {activeLendBookings.map((booking: any) => {
                        const actionButton = getLendBookingAction(booking.status);
                        return (
                          <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden">
                            <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                              <img src={booking.tool.primaryImageUrl} alt={booking.tool.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="flex-1 p-3 sm:p-4">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                      {booking.tool.name}
                                    </Link>
                                    {booking.totalPriceCents && (
                                      <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                        {formatPrice(booking.totalPriceCents)}
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                    <span className="inline-flex items-center gap-1">
                                      <Calendar className="h-4 w-4 text-gray-400" />
                                      <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                                    </span>
                                    {renderStatusBadge(getLendBadgeStatus(booking.status))}
                                  </div>
                                  <div className="mt-2 flex items-start gap-2">
                                    <Link
                                      to={`/profile/${booking.bookingUser.id}`}
                                      className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                                    >
                                      {booking.bookingUser.avatarImageUrl ? (
                                        <img
                                          src={booking.bookingUser.avatarImageUrl}
                                          alt={booking.bookingUser.name}
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        <span>{booking.bookingUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                                      )}
                                    </Link>
                                    <div className="text-sm leading-tight">
                                      <p className="text-gray-500">
                                        Lejet af:{' '}
                                        <Link to={`/profile/${booking.bookingUser.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                          {booking.bookingUser.name}
                                        </Link>
                                      </p>
                                      <Link
                                        to={`/profile/${booking.bookingUser.id}?tab=reviews`}
                                        className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                      >
                                        <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                        <span>
                                          {booking.bookingUser.ratingAverage !== null && booking.bookingUser.reviewCount > 0
                                            ? booking.bookingUser.ratingAverage.toFixed(1)
                                            : 'Ingen anmeldelser'}
                                        </span>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                                <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                                  {booking.totalPriceCents && (
                                    <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                      {formatPrice(booking.totalPriceCents)}
                                    </div>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    fullWidth
                                    icon={<MessageSquare className="h-4 w-4 mr-2" />}
                                    isLoading={messageThreadLoadingBookingId === booking.id}
                                    onClick={() => handleOpenBookingConversation(booking)}
                                  >
                                    Send besked
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    fullWidth
                                    onClick={() => handleOpenBookingDialog(booking)}
                                  >
                                    Detaljer
                                  </Button>
                                  {!shouldHideLendActionButton(booking.status) && (
                                    <Button
                                      variant={actionButton.variant}
                                      size="sm"
                                      fullWidth
                                      icon={actionButton.icon}
                                      onClick={() => {
                                        if (actionButton.action === 'return') {
                                          handleOpenReturnDialog(booking);
                                          return;
                                        }
                                        if (actionButton.action === 'review') {
                                          handleOpenReviewDialog(booking);
                                          return;
                                        }
                                        handleOpenBookingDialog(booking);
                                      }}
                                    >
                                      {actionButton.label}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {activeLendBookingsTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Viser {((activeLendBookingsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(activeLendBookingsPage * ITEMS_PER_PAGE, activeLendBookingsTotal)} af {activeLendBookingsTotal}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setActiveLendBookingsPage(p => Math.max(1, p - 1))}
                            disabled={activeLendBookingsPage === 1}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <span className="text-sm text-gray-700">
                            Side {activeLendBookingsPage} af {activeLendBookingsTotalPages}
                          </span>
                          <button
                            onClick={() => setActiveLendBookingsPage(p => Math.min(activeLendBookingsTotalPages, p + 1))}
                            disabled={activeLendBookingsPage === activeLendBookingsTotalPages}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">Ingen aktive eller kommende bookinger</p>
                  </div>
                )}
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Afsluttet udlån</h2>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {completedLendBookingsTotal}
                  </span>
                </div>
                
                {completedBookingsLoading ? (
                  <div className="text-center py-6">
                    <p className="text-gray-500">Indlæser...</p>
                  </div>
                ) : completedLendBookings.length > 0 ? (
                  <>
                    <div className="space-y-4">
                      {completedLendBookings.map((booking: any) => {
                        const actionButton = getLendBookingAction(booking.status);
                        return (
                          <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden">
                            <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                              <img src={booking.tool.primaryImageUrl} alt={booking.tool.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="flex-1 p-3 sm:p-4">
                              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                      {booking.tool.name}
                                    </Link>
                                    {booking.totalPriceCents && (
                                      <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                        {formatPrice(booking.totalPriceCents)}
                                      </div>
                                    )}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                    <span className="inline-flex items-center gap-1">
                                      <Calendar className="h-4 w-4 text-gray-400" />
                                      <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                                    </span>
                                    {renderStatusBadge(getLendBadgeStatus(booking.status))}
                                  </div>
                                  <div className="mt-2 flex items-start gap-2">
                                    <Link
                                      to={`/profile/${booking.bookingUser.id}`}
                                      className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                                    >
                                      {booking.bookingUser.avatarImageUrl ? (
                                        <img
                                          src={booking.bookingUser.avatarImageUrl}
                                          alt={booking.bookingUser.name}
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        <span>{booking.bookingUser.name?.charAt(0).toUpperCase() || 'U'}</span>
                                      )}
                                    </Link>
                                    <div className="text-sm leading-tight">
                                      <p className="text-gray-500">
                                        Lejet af:{' '}
                                        <Link to={`/profile/${booking.bookingUser.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                          {booking.bookingUser.name}
                                        </Link>
                                      </p>
                                      <Link
                                        to={`/profile/${booking.bookingUser.id}?tab=reviews`}
                                        className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                      >
                                        <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                        <span>
                                          {booking.bookingUser.ratingAverage !== null && booking.bookingUser.reviewCount > 0
                                            ? booking.bookingUser.ratingAverage.toFixed(1)
                                            : 'Ingen anmeldelser'}
                                        </span>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                                <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                                  {booking.totalPriceCents && (
                                    <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                      {formatPrice(booking.totalPriceCents)}
                                    </div>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    fullWidth
                                    icon={<MessageSquare className="h-4 w-4 mr-2" />}
                                    isLoading={messageThreadLoadingBookingId === booking.id}
                                    onClick={() => handleOpenBookingConversation(booking)}
                                  >
                                    Send besked
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    fullWidth
                                    onClick={() => handleOpenBookingDialog(booking)}
                                  >
                                    Detaljer
                                  </Button>
                                  {!shouldHideLendActionButton(booking.status) && (
                                    <Button
                                      variant={actionButton.variant}
                                      size="sm"
                                      fullWidth
                                      icon={actionButton.icon}
                                      onClick={() => {
                                        if (actionButton.action === 'return') {
                                          handleOpenReturnDialog(booking);
                                          return;
                                        }
                                        if (actionButton.action === 'review') {
                                          handleOpenReviewDialog(booking);
                                          return;
                                        }
                                        handleOpenBookingDialog(booking);
                                      }}
                                    >
                                      {actionButton.label}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {completedLendBookingsTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Viser {((completedLendBookingsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(completedLendBookingsPage * ITEMS_PER_PAGE, completedLendBookingsTotal)} af {completedLendBookingsTotal}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCompletedLendBookingsPage(p => Math.max(1, p - 1))}
                            disabled={completedLendBookingsPage === 1}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <span className="text-sm text-gray-700">
                            Side {completedLendBookingsPage} af {completedLendBookingsTotalPages}
                          </span>
                          <button
                            onClick={() => setCompletedLendBookingsPage(p => Math.min(completedLendBookingsTotalPages, p + 1))}
                            disabled={completedLendBookingsPage === completedLendBookingsTotalPages}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">Ingen afsluttede udlån</p>
                  </div>
                )}
              </div>
            </div>
          )}

            {/* Borrow Bookings Tab */}
            {activeTab === 'borrow_bookings' && (
            <div ref={borrowBookingsRef}>
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Lån</h1>
                <Link to="/all-borrow-bookings">
                  <Button variant="outline">Se alle lån</Button>
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-lg font-medium text-gray-900">Aktive & kommende lån</h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {activeBorrowBookingsTotal}
                    </span>
                  </div>

                  {activeBorrowBookingsLoading ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Indlæser...</p>
                    </div>
                  ) : activeBorrowBookings.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {activeBorrowBookings.map((booking: any) => {
                          const actionButton = getBorrowBookingAction(booking.status);
                          const owner = booking.owningUser;
                          const reminders = getBorrowReminders(booking);

                          return (
                            <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden">
                              <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                                {booking.tool.primaryImageUrl ? (
                                  <img src={booking.tool.primaryImageUrl} alt={booking.tool.name} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full bg-gray-100 flex items-center justify-center text-gray-400">
                                    <Hammer className="h-8 w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 p-3 sm:p-4">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                        {booking.tool.name}
                                      </Link>
                                      {booking.totalPriceCents && (
                                        <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                          {formatPrice(booking.totalPriceCents)}
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                      <span className="inline-flex items-center gap-1">
                                        <Calendar className="h-4 w-4 text-gray-400" />
                                        <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                                      </span>
                                      {renderStatusBadge(getBorrowBadgeStatus(booking.status))}
                                    </div>
                                    <div className="mt-2 flex items-start gap-2">
                                      <Link
                                        to={`/profile/${owner?.id}`}
                                        className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                                      >
                                        {owner?.avatarImageUrl ? (
                                          <img
                                            src={owner.avatarImageUrl}
                                            alt={owner.name}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <span>{owner?.name?.charAt(0).toUpperCase() || 'U'}</span>
                                        )}
                                      </Link>
                                      <div className="text-sm leading-tight">
                                        <p className="text-gray-500">
                                          Ejet af:{' '}
                                          <Link to={`/profile/${owner?.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                            {owner?.name || 'Ukendt bruger'}
                                          </Link>
                                        </p>
                                        <Link
                                          to={`/profile/${owner?.id}?tab=reviews`}
                                          className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                        >
                                          <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                          <span>
                                            {owner?.ratingAverage !== null && owner?.reviewCount > 0
                                              ? owner.ratingAverage.toFixed(1)
                                              : 'Ingen anmeldelser'}
                                          </span>
                                        </Link>
                                      </div>
                                    </div>

                                    {reminders.length > 0 && (
                                      <div className="mt-3 space-y-2">
                                        {reminders.map((reminder) => (
                                          <div
                                            key={reminder.key}
                                            className={`rounded-md border px-3 py-2 ${
                                              reminder.key === 'pickup'
                                                ? 'bg-blue-50 border-blue-200 text-blue-800'
                                                : reminder.key === 'return'
                                                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                  : 'bg-red-50 border-red-200 text-red-800'
                                            }`}
                                          >
                                            <p className="inline-flex items-center gap-1 text-sm font-medium">
                                              <Calendar className="h-4 w-4" />
                                              {reminder.title}
                                            </p>
                                            <p className="mt-0.5 text-xs">{reminder.message}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                                    {booking.totalPriceCents && (
                                      <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                        {formatPrice(booking.totalPriceCents)}
                                      </div>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      fullWidth
                                      icon={<MessageSquare className="h-4 w-4 mr-2" />}
                                      isLoading={messageThreadLoadingBookingId === booking.id}
                                      onClick={() => handleOpenBookingConversation(booking)}
                                    >
                                      Send besked
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      fullWidth
                                      onClick={() => handleOpenBookingDialog(booking, 'borrow')}
                                    >
                                      Detaljer
                                    </Button>
                                    {actionButton && (
                                      <Button
                                        variant={actionButton.variant}
                                        size="sm"
                                        fullWidth
                                        icon={actionButton.icon}
                                        isLoading={borrowActionLoadingBookingId === booking.id}
                                        onClick={() => {
                                          if (actionButton.action === 'cancel') {
                                            handleOpenBorrowActionDialog(booking, 'cancel');
                                            return;
                                          }
                                          if (actionButton.action === 'pickup') {
                                            handleOpenBorrowActionDialog(booking, 'pickup');
                                            return;
                                          }
                                          if (actionButton.action === 'complete') {
                                            handleOpenBorrowReviewDialog(booking);
                                            return;
                                          }
                                          handleOpenBookingDialog(booking, 'borrow');
                                        }}
                                      >
                                        {actionButton.label}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {activeBorrowBookingsTotalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-500">
                            Viser {((activeBorrowBookingsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(activeBorrowBookingsPage * ITEMS_PER_PAGE, activeBorrowBookingsTotal)} af {activeBorrowBookingsTotal}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setActiveBorrowBookingsPage(p => Math.max(1, p - 1))}
                              disabled={activeBorrowBookingsPage === 1}
                              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>
                            <span className="text-sm text-gray-700">
                              Side {activeBorrowBookingsPage} af {activeBorrowBookingsTotalPages}
                            </span>
                            <button
                              onClick={() => setActiveBorrowBookingsPage(p => Math.min(activeBorrowBookingsTotalPages, p + 1))}
                              disabled={activeBorrowBookingsPage === activeBorrowBookingsTotalPages}
                              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Ingen aktive eller kommende lån</p>
                    </div>
                  )}
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-lg font-medium text-gray-900">Afsluttede lån</h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {completedBorrowBookingsTotal}
                    </span>
                  </div>

                  {completedBorrowBookingsLoading ? (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Indlæser...</p>
                    </div>
                  ) : completedBorrowBookings.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {completedBorrowBookings.map((booking: any) => {
                          const actionButton = getBorrowBookingAction(booking.status);
                          const owner = booking.owningUser;
                          const reminders = getBorrowReminders(booking);

                          return (
                            <div key={booking.id} className="flex flex-col sm:flex-row border rounded-lg overflow-hidden">
                              <div className="w-full sm:w-44 aspect-[4/3] sm:aspect-square flex-shrink-0">
                                {booking.tool.primaryImageUrl ? (
                                  <img src={booking.tool.primaryImageUrl} alt={booking.tool.name} className="h-full w-full object-cover" />
                                ) : (
                                  <div className="h-full w-full bg-gray-100 flex items-center justify-center text-gray-400">
                                    <Hammer className="h-8 w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 p-3 sm:p-4">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <Link to={`/tool/${booking.tool.id}`} className="font-medium text-gray-900 hover:text-green-700 hover:underline">
                                        {booking.tool.name}
                                      </Link>
                                      {booking.totalPriceCents && (
                                        <div className="text-sm font-medium text-gray-900 sm:hidden whitespace-nowrap">
                                          {formatPrice(booking.totalPriceCents)}
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                      <span className="inline-flex items-center gap-1">
                                        <Calendar className="h-4 w-4 text-gray-400" />
                                        <span>{formatDate(booking.fromDate)} - {formatDate(booking.toDate)}</span>
                                      </span>
                                      {renderStatusBadge(getBorrowBadgeStatus(booking.status))}
                                    </div>
                                    <div className="mt-2 flex items-start gap-2">
                                      <Link
                                        to={`/profile/${owner?.id}`}
                                        className="h-7 w-7 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-medium text-xs overflow-hidden hover:ring-2 hover:ring-green-200"
                                      >
                                        {owner?.avatarImageUrl ? (
                                          <img
                                            src={owner.avatarImageUrl}
                                            alt={owner.name}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <span>{owner?.name?.charAt(0).toUpperCase() || 'U'}</span>
                                        )}
                                      </Link>
                                      <div className="text-sm leading-tight">
                                        <p className="text-gray-500">
                                          Ejet af:{' '}
                                          <Link to={`/profile/${owner?.id}`} className="text-gray-900 hover:text-green-700 hover:underline">
                                            {owner?.name || 'Ukendt bruger'}
                                          </Link>
                                        </p>
                                        <Link
                                          to={`/profile/${owner?.id}?tab=reviews`}
                                          className="mt-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-green-700"
                                        >
                                          <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                                          <span>
                                            {owner?.ratingAverage !== null && owner?.reviewCount > 0
                                              ? owner.ratingAverage.toFixed(1)
                                              : 'Ingen anmeldelser'}
                                          </span>
                                        </Link>
                                      </div>
                                    </div>

                                    {reminders.length > 0 && (
                                      <div className="mt-3 space-y-2">
                                        {reminders.map((reminder) => (
                                          <div
                                            key={reminder.key}
                                            className={`rounded-md border px-3 py-2 ${
                                              reminder.key === 'pickup'
                                                ? 'bg-blue-50 border-blue-200 text-blue-800'
                                                : reminder.key === 'return'
                                                  ? 'bg-amber-50 border-amber-200 text-amber-800'
                                                  : 'bg-red-50 border-red-200 text-red-800'
                                            }`}
                                          >
                                            <p className="inline-flex items-center gap-1 text-sm font-medium">
                                              <Calendar className="h-4 w-4" />
                                              {reminder.title}
                                            </p>
                                            <p className="mt-0.5 text-xs">{reminder.message}</p>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="w-full sm:w-36 flex flex-col items-stretch sm:items-end gap-2">
                                    {booking.totalPriceCents && (
                                      <div className="hidden sm:block text-sm font-medium text-gray-900 sm:text-right">
                                        {formatPrice(booking.totalPriceCents)}
                                      </div>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      fullWidth
                                      icon={<MessageSquare className="h-4 w-4 mr-2" />}
                                      isLoading={messageThreadLoadingBookingId === booking.id}
                                      onClick={() => handleOpenBookingConversation(booking)}
                                    >
                                      Send besked
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      fullWidth
                                      onClick={() => handleOpenBookingDialog(booking, 'borrow')}
                                    >
                                      Detaljer
                                    </Button>
                                    {actionButton && (
                                      <Button
                                        variant={actionButton.variant}
                                        size="sm"
                                        fullWidth
                                        icon={actionButton.icon}
                                        isLoading={borrowActionLoadingBookingId === booking.id}
                                        onClick={() => {
                                          if (actionButton.action === 'cancel') {
                                            handleOpenBorrowActionDialog(booking, 'cancel');
                                            return;
                                          }
                                          if (actionButton.action === 'pickup') {
                                            handleOpenBorrowActionDialog(booking, 'pickup');
                                            return;
                                          }
                                          if (actionButton.action === 'complete') {
                                            handleOpenBorrowReviewDialog(booking);
                                            return;
                                          }
                                          handleOpenBookingDialog(booking, 'borrow');
                                        }}
                                      >
                                        {actionButton.label}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {completedBorrowBookingsTotalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                          <div className="text-sm text-gray-500">
                            Viser {((completedBorrowBookingsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(completedBorrowBookingsPage * ITEMS_PER_PAGE, completedBorrowBookingsTotal)} af {completedBorrowBookingsTotal}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCompletedBorrowBookingsPage(p => Math.max(1, p - 1))}
                              disabled={completedBorrowBookingsPage === 1}
                              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronLeft className="h-5 w-5" />
                            </button>
                            <span className="text-sm text-gray-700">
                              Side {completedBorrowBookingsPage} af {completedBorrowBookingsTotalPages}
                            </span>
                            <button
                              onClick={() => setCompletedBorrowBookingsPage(p => Math.min(completedBorrowBookingsTotalPages, p + 1))}
                              disabled={completedBorrowBookingsPage === completedBorrowBookingsTotalPages}
                              className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-500">Ingen afsluttede lån</p>
                    </div>
                  )}
              </div>
            </div>
          )}
          
          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div ref={messagesRef}>
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Beskeder</h1>
                <Link to="/all-message-threads" className="text-sm text-green-600 font-medium">
                  Se alt
                </Link>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {messagesLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Indlæser...</p>
                  </div>
                ) : messageThreads.length > 0 ? (
                  <>
                    <div>
                      {messageThreads.map((thread: any, index: number) => {
                        const otherParticipant = thread.participants?.find((participant: any) => participant.id !== user?.id) || thread.latestMessageSentBy;
                        const isOtherParticipantOnline = !!otherParticipant?.isOnline;
                        const senderAvatarSrc = otherParticipant?.avatarImageUrl || otherParticipant?.avatar;
                        return (
                          <Link
                            key={thread.id}
                            to={`/conversation/${thread.id}`}
                            className={`block p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200 ${
                              index !== messageThreads.length - 1 ? 'border-b border-gray-200' : ''
                            } ${thread.unreadMessageCount > 0 ? 'bg-green-50' : ''}`}
                          >
                            <div className="flex items-start">
                              <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium mr-4 overflow-hidden">
                                {senderAvatarSrc ? (
                                  <img
                                    src={senderAvatarSrc}
                                    alt={otherParticipant?.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span>{otherParticipant?.name?.charAt(0).toUpperCase() || 'U'}</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start mb-1">
                                  <h3 className="text-sm font-medium text-gray-900">{thread.subject}</h3>
                                  <p className="text-xs text-gray-500">{formatDate(thread.latestMessage?.sentAt)}</p>
                                </div>
                                <p className="text-xs text-gray-500 mb-1">{otherParticipant?.name}</p>
                                <p className="text-sm text-gray-600 line-clamp-2">{thread.latestMessage?.content}</p>
                              </div>
                              <div className="ml-4 flex flex-row items-center gap-2">
                                {isOtherParticipantOnline && (
                                  <span className="inline-block h-3 w-3 rounded-full bg-green-600"></span>
                                )}
                                {thread.unreadMessageCount > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {thread.unreadMessageCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    
                    {messageThreadsTotalPages > 1 && (
                      <div className="flex items-center justify-between p-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Viser {((messagesPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(messagesPage * ITEMS_PER_PAGE, messageThreadsTotal)} af {messageThreadsTotal}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setMessagesPage(p => Math.max(1, p - 1))}
                            disabled={messagesPage === 1}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <span className="text-sm text-gray-700">
                            Side {messagesPage} af {messageThreadsTotalPages}
                          </span>
                          <button
                            onClick={() => setMessagesPage(p => Math.min(messageThreadsTotalPages, p + 1))}
                            disabled={messagesPage === messageThreadsTotalPages}
                            className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto h-12 w-12 text-gray-400">
                      <MessageSquare className="h-12 w-12" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Ingen beskeder</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Du har endnu ingen beskeder endnu.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recent Activity */}
          <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Seneste aktivitet</h2>
              <Link to="/history" className="text-sm text-green-600 font-medium">
                Se alt
              </Link>
            </div>

            {eventsLoading ? (
              <div className="text-center py-6">
                <p className="text-gray-500">Indlæser aktivitet...</p>
              </div>
            ) : events.length > 0 ? (
              <>
                <div className="space-y-6">
                  {events.map((event: any) => {
                    const iconConfig = getEventIconConfig(event.eventType);
                    const EventIcon = iconConfig.icon;

                    return (
                      <div key={event.id} className="flex">
                        <div className="mr-4">
                          <div className={`${iconConfig.containerClass} rounded-full p-2`}>
                            <EventIcon className="h-5 w-5" />
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">{event.eventText}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(event.eventDatetime)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {eventsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500">
                      Viser {((eventsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(eventsPage * ITEMS_PER_PAGE, eventsTotal)} af {eventsTotal}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEventsPage(p => Math.max(1, p - 1))}
                        disabled={eventsPage === 1}
                        className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <span className="text-sm text-gray-700">
                        Side {eventsPage} af {eventsTotalPages}
                      </span>
                      <button
                        onClick={() => setEventsPage(p => Math.min(eventsTotalPages, p + 1))}
                        disabled={eventsPage === eventsTotalPages}
                        className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500">Ingen seneste aktivitet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Confirmation Dialog */}
      <Dialog
        isOpen={!!selectedBooking}
        onClose={() => setSelectedBooking(null)}
        title="Bookingdetaljer"
        size="lg"
      >
        {selectedBooking && (
          <div className="p-6">
            {/* Booking Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Værktøj information</h3>
              <div className="flex gap-4 bg-gray-50 rounded-lg p-4">
                <img 
                  src={selectedBooking.tool.primaryImageUrl} 
                  alt={selectedBooking.tool.name}
                  className="w-24 h-24 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{selectedBooking.tool.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{selectedBooking.tool.description}</p>
                  <div className="mt-2 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-4">
                    <span className="text-sm text-gray-500">
                      {formatDate(selectedBooking.fromDate)} - {formatDate(selectedBooking.toDate)}
                    </span>
                    {renderStatusBadge(
                      selectedBookingMode === 'lend'
                        ? getLendBadgeStatus(selectedBooking.status)
                        : getBorrowBadgeStatus(selectedBooking.status)
                    )}
                  </div>
                  {selectedBooking.totalPriceCents && (
                    <div className="mt-2 text-lg font-semibold text-gray-900">
                      {formatPrice(selectedBooking.totalPriceCents)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Counterparty Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedBookingMode === 'borrow' ? 'Ejer information' : 'Lejer information'}
              </h3>
              <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium overflow-hidden">
                  {bookingDialogCounterparty?.avatarImageUrl ? (
                    <img 
                      src={bookingDialogCounterparty.avatarImageUrl} 
                      alt={bookingDialogCounterparty.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xl">{bookingDialogCounterparty?.name?.charAt(0).toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{bookingDialogCounterparty?.name || 'Ukendt bruger'}</h4>
                  {bookingDialogCounterparty?.ratingAverage !== null && bookingDialogCounterparty?.reviewCount > 0 ? (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="ml-1 text-sm font-medium text-gray-900">
                          {bookingDialogCounterparty.ratingAverage.toFixed(1)}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        ({bookingDialogCounterparty.reviewCount} anmeldelser)
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1">Ingen anmeldelser endnu</p>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="mb-6">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 mb-4 text-left text-gray-900 hover:text-gray-700"
                onClick={() => setIsBookingMessagesCollapsed((collapsed) => !collapsed)}
                aria-label={isBookingMessagesCollapsed ? 'Expandér beskeder' : 'Kollaps beskeder'}
              >
                <h3 className="text-lg font-semibold">Beskeder</h3>
                {isBookingMessagesCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>

              {!isBookingMessagesCollapsed && (
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {threadLoading ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Indlæser beskeder...</p>
                    </div>
                  ) : bookingMessages.length > 0 ? (
                    <div className="space-y-3">
                      {bookingMessages.map((message: any) => {
                        const isOwner = message.sender.id === user?.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwner ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                isOwner
                                  ? 'bg-green-600 text-white'
                                  : 'bg-white text-gray-900'
                              }`}
                            >
                              <p className="text-sm">{message.content}</p>
                              <p className={`text-xs mt-1 ${
                                isOwner ? 'text-green-100' : 'text-gray-500'
                              }`}>
                                {formatDate(message.sentAt)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Ingen beskeder endnu</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {selectedBookingMode === 'lend' && selectedBooking.status === 'requested' ? (
              <div className="flex gap-4 justify-end pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowDeclineDialog(true)}
                >
                  Afvis
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setShowConfirmDialog(true)}
                >
                  Bekræft
                </Button>
              </div>
            ) : (
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setSelectedBooking(null)}
                >
                  Luk
                </Button>
              </div>
            )}
          </div>
        )}
      </Dialog>

      {/* Return Booking Dialog */}
      <Dialog
        isOpen={!!returnBooking && !showReturnDamageDialog}
        onClose={handleCloseReturnDialog}
        title="Er værktøj returneret?"
        size="md"
      >
        {returnBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Vælg hvordan returneringen skal registreres for denne booking.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{returnBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(returnBooking.fromDate)} - {formatDate(returnBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (valgfri)
              </label>
              <textarea
                value={returnBookingNote}
                onChange={(event) => setReturnBookingNote(event.target.value)}
                placeholder="Tilføj evt. detaljer om returneringen..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={returnBookingLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReturnDialog}
                disabled={returnBookingLoading}
              >
                Annuller
              </Button>
              <Button
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={handleOpenReturnDamageDialog}
                isLoading={returnBookingLoading && returnBookingAction === 'issues'}
                disabled={returnBookingLoading}
              >
               Ja, men med skader/mangler
              </Button>
              <Button
                variant="primary"
                onClick={() => handleMarkReturned(false)}
                isLoading={returnBookingLoading && returnBookingAction === 'clean'}
                disabled={returnBookingLoading}
              >
                Ja, i samme stand
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!returnBooking && showReturnDamageDialog}
        onClose={handleCloseReturnDamageDialog}
        title="Rapportér skader/mangler"
        size="md"
      >
        {returnBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Beskriv skaden eller manglen og upload billeder som dokumentation.
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Vores support kigger på sagen og vender tilbage hurtigst muligt.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{returnBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(returnBooking.fromDate)} - {formatDate(returnBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kommentar
              </label>
              <textarea
                value={returnDamageComment}
                onChange={(event) => setReturnDamageComment(event.target.value)}
                placeholder="Beskriv skaden eller manglen..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={returnBookingLoading}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billeder (mindst 1)
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <input
                  id="return-damage-files-dashboard"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleReturnDamageFileSelect}
                  disabled={returnBookingLoading}
                  className="sr-only"
                />
                <label
                  htmlFor="return-damage-files-dashboard"
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium w-fit ${
                    returnBookingLoading
                      ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                      : 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Vælg billeder
                </label>
                <span className="text-sm text-gray-600">
                  {returnDamageFiles.length > 0
                    ? `${returnDamageFiles.length} fil${returnDamageFiles.length > 1 ? 'er' : ''} valgt`
                    : 'Ingen filer valgt'}
                </span>
              </div>
            </div>

            {returnDamageFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {returnDamageFiles.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                    <p className="text-sm text-gray-700 truncate pr-3">{file.name}</p>
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-700"
                      onClick={() => handleRemoveReturnDamageFile(index)}
                      disabled={returnBookingLoading}
                    >
                      Fjern
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReturnDamageDialog}
                disabled={returnBookingLoading}
              >
                Tilbage
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitReturnedWithDamages}
                isLoading={returnBookingLoading && returnBookingAction === 'issues'}
                disabled={
                  returnBookingLoading ||
                  !returnDamageComment.trim() ||
                  returnDamageFiles.length === 0
                }
              >
                Send til support
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!reviewBooking}
        onClose={handleCloseReviewDialog}
        title="Afslut udlån"
        size="md"
      >
        {reviewBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Giv bedømmelse af lejeren og afslut bookingen.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{reviewBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(reviewBooking.fromDate)} - {formatDate(reviewBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Antal stjerner (valgfri)</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReviewRating(value)}
                    className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={reviewBookingLoading}
                    aria-label={`${value} stjerner`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        value <= reviewRating ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {reviewRating > 0 ? `${reviewRating} ud af 5 stjerner valgt` : 'Ingen stjerner valgt endnu'}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kommentar (valgfri)</label>
              <textarea
                value={reviewComment}
                onChange={(event) => setReviewComment(event.target.value)}
                placeholder="Skriv en kommentar til lejeren..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={reviewBookingLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseReviewDialog}
                disabled={reviewBookingLoading}
              >
                Annuller
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitOwnerReview}
                isLoading={reviewBookingLoading}
                disabled={reviewBookingLoading}
              >
                Afslut
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!borrowReviewBooking}
        onClose={handleCloseBorrowReviewDialog}
        title="Afslut lån"
        size="md"
      >
        {borrowReviewBooking && (
          <div className="p-6">
            <p className="text-sm text-gray-600">
              Giv evt. en bedømmelse af udlejeren og afslut lånet.
            </p>

            <div className="mt-4 rounded-lg bg-gray-50 p-3">
              <p className="text-sm font-medium text-gray-900">{borrowReviewBooking.tool?.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatDate(borrowReviewBooking.fromDate)} - {formatDate(borrowReviewBooking.toDate)}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Antal stjerner (valgfri)</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setBorrowReviewRating(value)}
                    className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
                    disabled={borrowReviewLoading}
                    aria-label={`${value} stjerner`}
                  >
                    <Star
                      className={`h-7 w-7 ${
                        value <= borrowReviewRating ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {borrowReviewRating > 0 ? `${borrowReviewRating} ud af 5 stjerner valgt` : 'Ingen stjerner valgt endnu'}
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Kommentar (valgfri)</label>
              <textarea
                value={borrowReviewComment}
                onChange={(event) => setBorrowReviewComment(event.target.value)}
                placeholder="Skriv en kommentar til udlejeren..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                disabled={borrowReviewLoading}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCloseBorrowReviewDialog}
                disabled={borrowReviewLoading}
              >
                Annuller
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitBorrowReview}
                isLoading={borrowReviewLoading}
                disabled={borrowReviewLoading}
              >
                Afslut
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Borrow Action Dialog */}
      <ConfirmDialog
        isOpen={!!borrowActionDialog}
        onClose={handleCloseBorrowActionDialog}
        onConfirm={handleConfirmBorrowAction}
        title={borrowActionDialog?.action === 'cancel' ? 'Annuller lån' : 'Bekræft afhentning'}
        description={
          borrowActionDialog?.action === 'cancel'
            ? 'Er du sikker på, at du vil annullere denne låneanmodning?'
            : 'Bekræft at værktøjet er afhentet.\n\nHar du tjekket, at alt tilbehør er med, og at værktøjets tilstand er som beskrevet?'
        }
        confirmLabel={borrowActionDialog?.action === 'cancel' ? 'Annuller lån' : 'Marker som afhentet'}
        confirmButtonVariant="primary"
        showMessageInput={borrowActionDialog?.action === 'cancel'}
        messagePlaceholder="Skriv en kommentar (valgfri)..."
        isLoading={borrowActionLoading}
      />

      {/* Confirm Booking Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmBooking}
        title="Bekræft booking"
        description="Er du sikker på, at du vil bekræfte denne booking?"
        confirmLabel="Bekræft"
        confirmButtonVariant="primary"
        showMessageInput={true}
        messagePlaceholder="Skriv en besked til lejeren (valgfri)..."
        isLoading={confirmLoading}
      />

      {/* Decline Booking Dialog */}
      <ConfirmDialog
        isOpen={showDeclineDialog}
        onClose={() => setShowDeclineDialog(false)}
        onConfirm={handleDeclineBooking}
        title="Afvis booking"
        description="Er du sikker på, at du vil afvise denne booking?"
        confirmLabel="Afvis"
        confirmButtonVariant="primary"
        showMessageInput={true}
        messagePlaceholder="Skriv en grund til afvisning (valgfri)..."
        isLoading={declineLoading}
      />
    </div>
  );
};

export default Dashboard;